/* Real Liberty Copper — coin reservation UI driver.
 *
 * Two flows live on the product page:
 *
 *   RANDOM    → click "Reserve" → POST /apps/coin-lookup/coin-reserve-random
 *               → on success, add to cart with private `_reservation_token`
 *               → customer learns the serial only on unbox
 *
 *   SPECIFIC  → type 7-digit serial → GET /apps/coin-lookup/coin-lookup
 *               → if available, click "Reserve this coin" → POST
 *               → /apps/coin-lookup/coin-reserve → add to cart with both
 *               → `Serial Number` (public) and `_reservation_token` (private)
 *
 * Architecture notes:
 *   - THE CART IS JS-ONLY. The forms deliberately do NOT post to /cart/add
 *     (action="" posts harmlessly back to the page) and the serial input is
 *     NAMELESS — the serial + token line-item properties are attached
 *     exclusively by this script, after the reserve API succeeds. History:
 *     the old markup let a native submit (Enter key, no-JS surfaces) ship a
 *     raw unreserved serial to checkout, which double-sold three serials
 *     before it was caught (2026-08-05). Never reintroduce a named serial
 *     input or a /cart/add action on these forms.
 *   - This script also DE-ARMS any stale markup: on init it strips the
 *     name attribute from the serial input and hijacks the form's submit
 *     (Enter now triggers Check availability / Reserve — the intended UX).
 *   - Tokens are UUIDs minted client-side; we never share them across
 *     reservations. Stored in sessionStorage keyed by serial (specific) or
 *     a singleton key (random). Cleared on cart-add success.
 *   - All endpoints live under the App Proxy path the user installed.
 *     Path-prefix is configured in shopify.app.toml — we read it from a
 *     data-attribute on the form root so the JS doesn't hard-code it.
 *
 * The script is namespaced (`window.RLC`) so it can be loaded multiple
 * times without re-binding handlers, and so the theme editor's section
 * re-render doesn't double-fire events.
 */

(function () {
  "use strict";

  if (window.RLC && window.RLC.initialized) return;
  window.RLC = window.RLC || {};
  window.RLC.initialized = true;

  // ── Config ────────────────────────────────────────────────────────────
  // Customers may type 4–7 digits; the server normalizes to the canonical
  // 7-digit zero-padded serial and echoes it back. We use the server's value
  // for the cart property + sessionStorage so it always matches the DB.
  const SERIAL_INPUT_RE = /^[0-9]{4,7}$/;
  const RANDOM_TOKEN_KEY = "rlc.random_token";

  function specificTokenKey(serial) {
    return `rlc.specific_token.${serial}`;
  }

  // Pre-order display config, read from data-attributes on the specific-form
  // root with sensible defaults so pre-order works even on sections that don't
  // pass the settings. {days} is interpolated into the message.
  function preorderConfig(root) {
    const enabledAttr = root.getAttribute("data-rlc-preorder-enabled");
    const days = root.getAttribute("data-rlc-preorder-days") || "30";
    const msgTpl =
      root.getAttribute("data-rlc-preorder-message") ||
      "Available to reserve.";
    return {
      enabled: enabledAttr !== "false", // default on
      days: days,
      message: msgTpl.replace("{days}", days),
      availableLabel: root.getAttribute("data-rlc-available-label") || "Order this Coin",
      preorderLabel: root.getAttribute("data-rlc-preorder-label") || "Reserve This Coin",
    };
  }

  function uuid() {
    if (crypto.randomUUID) return crypto.randomUUID();
    // Fallback for older browsers; v4-ish, not cryptographically perfect
    // but the server validates format and the token is single-use.
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
      const r = (Math.random() * 16) | 0;
      const v = c === "x" ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }

  // ── Network ───────────────────────────────────────────────────────────
  async function apiGet(base, path, query) {
    const qs = new URLSearchParams(query).toString();
    const r = await fetch(`${base}/${path}${qs ? `?${qs}` : ""}`, {
      method: "GET",
      headers: { accept: "application/json" },
      credentials: "same-origin",
    });
    return r.json();
  }

  async function apiPost(base, path, body) {
    const r = await fetch(`${base}/${path}`, {
      method: "POST",
      headers: { "content-type": "application/json", accept: "application/json" },
      credentials: "same-origin",
      body: JSON.stringify(body),
    });
    return r.json();
  }

  // Shopify Cart API. Properties prefixed `_` are hidden from cart UI.
  // Requests the cart-drawer section back (Section Rendering API) so we can
  // open the drawer with fresh server-rendered HTML.
  async function addToCart(variantId, properties) {
    const r = await fetch("/cart/add.js", {
      method: "POST",
      headers: { "content-type": "application/json", accept: "application/json" },
      body: JSON.stringify({
        items: [{ id: Number(variantId), quantity: 1, properties }],
        sections: "cart-drawer",
        sections_url: location.pathname,
      }),
    });
    if (!r.ok) {
      let msg = "Could not add to cart.";
      try { const j = await r.json(); msg = j.description || j.message || msg; } catch (_) {}
      throw new Error(msg);
    }
    if (window.rlcStampLanding) window.rlcStampLanding();
    return r.json();
  }

  // Open the slide-out cart with the section HTML from an add response. Falls
  // back to the full cart page if the drawer/Alpine isn't available.
  function openDrawer(addResponse) {
    const store = window.Alpine && window.Alpine.store && window.Alpine.store("cartDrawer");
    const html = addResponse && addResponse.sections && addResponse.sections["cart-drawer"];
    if (store) {
      if (html) store.openWith(html); else store.openFresh();
    } else {
      window.location.href = "/cart";
    }
  }

  // ── DOM helpers ──────────────────────────────────────────────────────
  function setStatus(root, kind, text) {
    const el = root.querySelector("[data-rlc-status]");
    if (!el) return;
    el.textContent = text || "";
    el.setAttribute("data-rlc-kind", kind || "");
    el.setAttribute("aria-live", "polite");
  }

  function setBusy(root, busy) {
    root.querySelectorAll("button, input").forEach((el) => {
      el.disabled = !!busy;
    });
    root.setAttribute("data-rlc-busy", busy ? "true" : "false");
  }

  function show(el) { if (el) el.hidden = false; }
  function hide(el) { if (el) el.hidden = true; }

  // ── Random flow ──────────────────────────────────────────────────────
  function initRandomForm(form) {
    const root = form;
    const base = root.getAttribute("data-rlc-proxy-base"); // e.g. "/apps/coin-lookup"
    const variantId = root.getAttribute("data-rlc-variant-id");
    if (!base || !variantId) return;

    form.addEventListener("submit", async (e) => {
      e.preventDefault();
      setStatus(root, "loading", "Reserving your coin…");
      setBusy(root, true);

      const token = uuid();
      try {
        const res = await apiPost(base, "coin-reserve-random", { token });
        if (!res.success) {
          setStatus(root, "error", res.message || "Could not reserve. Please try again.");
          setBusy(root, false);
          return;
        }
        sessionStorage.setItem(RANDOM_TOKEN_KEY, token);

        const cart = await addToCart(variantId, {
          _reservation_token: token,
          _reserved_at: new Date().toISOString(),
        });
        setStatus(root, "success", "Reserved!");
        openDrawer(cart);
        setBusy(root, false);
      } catch (err) {
        setStatus(root, "error", err.message || "Unexpected error. Please try again.");
        setBusy(root, false);
      }
    });
  }

  // ── Specific flow ────────────────────────────────────────────────────
  function initSpecificForm(form) {
    const root = form;
    const base = root.getAttribute("data-rlc-proxy-base");
    const variantId = root.getAttribute("data-rlc-variant-id");
    if (!base || !variantId) return;

    const input = root.querySelector("[data-rlc-serial-input]");
    const lookupBtn = root.querySelector("[data-rlc-lookup-btn]");
    const reserveBtn = root.querySelector("[data-rlc-reserve-btn]");
    const reserveWrap = root.querySelector("[data-rlc-reserve-wrap]");
    if (!input || !lookupBtn) return;

    // DE-ARM: the serial input must never be a submittable form field. Any
    // stale/cached markup that still carries name="properties[Serial Number]"
    // gets it stripped here, so a native form post can never smuggle an
    // unreserved serial into the cart (the 2026-08-05 double-sale path).
    input.removeAttribute("name");

    // HIJACK native submission: Enter in the serial field used to POST the
    // raw form to /cart/add — now it drives the intended flow instead
    // (reserve if a looked-up coin is ready, otherwise check availability).
    // On the premium PDP the root is a <div>, where submit never fires —
    // harmless.
    root.addEventListener("submit", (e) => {
      e.preventDefault();
      if (root.getAttribute("data-rlc-busy") === "true") return;
      if (reserveWrap && !reserveWrap.hidden && reserveBtn) {
        reserveBtn.click();
      } else {
        lookupBtn.click();
      }
    });

    const cfg = preorderConfig(root);
    // Remember the reserve button's authored label so we can restore it.
    const defaultReserveLabel = reserveBtn ? reserveBtn.textContent.trim() : "";

    hide(reserveWrap);

    // Typing a new serial invalidates a previously looked-up result — hide
    // the reserve button until the new value is checked.
    input.addEventListener("input", () => {
      if (reserveBtn && reserveBtn.dataset.rlcSerial &&
          input.value.trim() !== reserveBtn.dataset.rlcSerial.replace(/^0+/, "") &&
          input.value.trim() !== reserveBtn.dataset.rlcSerial) {
        hide(reserveWrap);
        setStatus(root, "", "");
      }
    });

    // ── Lookup ──
    lookupBtn.addEventListener("click", async (e) => {
      e.preventDefault();
      const typed = (input.value || "").trim();
      if (!SERIAL_INPUT_RE.test(typed)) {
        setStatus(root, "error", "Please enter a 4–7 digit serial number.");
        hide(reserveWrap);
        return;
      }
      setStatus(root, "loading", "Checking…");
      setBusy(root, true);
      hide(reserveWrap);

      try {
        const res = await apiGet(base, "coin-lookup", { serial: typed });
        // The API returns { status, serial, available }. `serial` is the
        // normalized 7-digit form — always reserve/cart-add with THAT, never
        // the raw input. (Older cached responses without `status` fall back to
        // the `available` boolean.)
        const serial = (res && res.serial) || typed;
        const status =
          res && res.status
            ? res.status
            : res && res.available === true
            ? "available"
            : "unavailable";

        if (status === "available") {
          setStatus(root, "success", `Coin #${serial} is available.`);
          if (reserveBtn) {
            reserveBtn.dataset.rlcSerial = serial;
            reserveBtn.dataset.rlcMode = "available";
            reserveBtn.textContent = cfg.availableLabel || defaultReserveLabel;
          }
          show(reserveWrap);
        } else if (status === "preorder" && cfg.enabled) {
          setStatus(root, "preorder", cfg.message);
          if (reserveBtn) {
            reserveBtn.dataset.rlcSerial = serial;
            reserveBtn.dataset.rlcMode = "preorder";
            reserveBtn.textContent = cfg.preorderLabel || defaultReserveLabel;
          }
          show(reserveWrap);
        } else if (status === "invalid_format") {
          setStatus(root, "error", res.message || "Please enter a valid serial number.");
        } else {
          setStatus(
            root, "info",
            "This serial number is not available. Please try another.",
          );
        }
      } catch (err) {
        setStatus(root, "error", "Connection issue. Please try again.");
      } finally {
        setBusy(root, false);
      }
    });

    // ── Reserve / Pre-Order ──
    if (reserveBtn) {
      reserveBtn.addEventListener("click", async (e) => {
        e.preventDefault();
        const serial = reserveBtn.dataset.rlcSerial;
        if (!serial || !/^[0-9]{7}$/.test(serial)) {
          setStatus(root, "error", "Please look up a coin first.");
          return;
        }
        const isPreorder = reserveBtn.dataset.rlcMode === "preorder";
        setStatus(root, "loading", `Reserving coin #${serial}…`);
        setBusy(root, true);

        const token = uuid();
        try {
          const res = await apiPost(base, "coin-reserve", { serial, token });
          if (!res.success) {
            setStatus(root, "info", res.message || "Coin is no longer available.");
            // Reservation may have just been taken; clear the reserve button
            hide(reserveWrap);
            setBusy(root, false);
            return;
          }
          // Use the server's normalized serial for cart + storage.
          const finalSerial = res.serial || serial;
          sessionStorage.setItem(specificTokenKey(finalSerial), token);

          const props = {
            "Serial Number": finalSerial,
            _reservation_token: token,
            _reserved_at: new Date().toISOString(),
          };
          // Hidden flag so the cart drawer can badge pre-order lines.
          if (isPreorder) props._preorder = "true";

          const cart = await addToCart(variantId, props);
          setStatus(root, "success", "Reserved!");
          openDrawer(cart);
          setBusy(root, false);
        } catch (err) {
          setStatus(root, "error", err.message || "Unexpected error. Please try again.");
          setBusy(root, false);
        }
      });
    }
  }

  // ── Boot ─────────────────────────────────────────────────────────────
  function bootAll() {
    document.querySelectorAll("[data-rlc-random-form]").forEach(initRandomForm);
    document.querySelectorAll("[data-rlc-specific-form]").forEach(initSpecificForm);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", bootAll);
  } else {
    bootAll();
  }

  // Theme editor reloads the section without a full page navigation — listen
  // for the event so the new DOM gets handlers attached.
  document.addEventListener("shopify:section:load", bootAll);
})();